import React, { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { isEmpty } from 'lodash';
import { useViralLoopsContext } from '../../../context/ViralLoops';
import errors from '../../../utils/errors';
import { trackWithFingerprint } from '../../../utils/trackWithFingerprint';
import { emailRegex } from '../../../utils/regex';
import { hidden } from '../../../styles/a11y';
import {
  EmailWidgetInput,
  MobileEmailButton,
  MobileContainer,
  EmailInputWrapper
} from './ViralLoopsWidget.styles';

import {
  DEFAULT_EMAIL_LABEL,
  DEFAULT_EMAIL_BUTTON_TEXT,
  THANKS_TEXT_WITH_SPACING,
  SharedProps,
  WaitlistEmailCaptureProps
} from '../shared';
import { CFColorTokenProp } from '../../../theme/tokens/contentful-color';
interface ViralLoopsWidgetInputProps {
  id?: string;
  placeholder?: string;
  className: string;
  ctaText: string;
  onChange: (val: string) => void;
  options: {};
  onFocus: () => void;
  onBlur: () => void;
  value: string;
  success: boolean;
  disabled: boolean;
}

interface ButtonProps {
  onClick: () => void;
}

interface Props extends SharedProps {
  ctaText?: string;
  button: (props: ButtonProps) => JSX.Element;
  returningUserButton: (props: ButtonProps) => JSX.Element;
  input: (props: ViralLoopsWidgetInputProps) => JSX.Element;
  setErrorMessage: Function;
}

export const SubmitEmailWidget = ({
  id,
  className,
  placeholderText = DEFAULT_EMAIL_LABEL,
  labelText = DEFAULT_EMAIL_LABEL,
  ctaText = DEFAULT_EMAIL_BUTTON_TEXT,
  button,
  returningUserButton,
  input,
  setErrorMessage
}: Props) => {
  const {
    setLoadVLScript,
    isReturningUser = false,
    VL,
    setIsReturningUser = () => {
      return false;
    }
  } = useViralLoopsContext();
  const [email, setEmail] = useState('');
  const [isLabelObscured, setIsLabelObscured] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const setFromEvent = () => setErrorMessage('');
    window.addEventListener('mousedown', setFromEvent);

    return () => {
      window.removeEventListener('mousedown', setFromEvent);
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailAddress = urlParams.get('emailAddress') ?? '';

    if (emailAddress !== '' && !isReturningUser) {
      setEmail(emailAddress);
    }
  }, []);

  useEffect(() => {
    setIsReturningUser(VL?.refCode !== undefined);
  }, [VL?.refCode]);

  useEffect(() => {
    setLoadVLScript?.(true);
  }, [setLoadVLScript]);

  const submit = () => {
    if (!email.match(emailRegex)) {
      setErrorMessage(errors.INVALID_EMAIL);
      return;
    }

    setSubmitted(true);
    setEmail('');

    if (isEmpty(VL)) {
      return;
    }

    VL.optionsInitialize();
    VL.options.form_fields.form_firstName = '';
    VL.options.form_fields.form_email = email;
    VL.createLead().then(() => {
      // Fire segment event
      trackWithFingerprint('Waitlist Selected', {
        waitlist_user_added: true
      });
      setIsReturningUser(true);
      VL.openModal();
    });
  };

  const inputProps: ViralLoopsWidgetInputProps = {
    id: id,
    placeholder: submitted ? THANKS_TEXT_WITH_SPACING : placeholderText,
    className: 'fs-block',
    ctaText: ctaText,
    onChange: (val: string) => setEmail(val),
    value: email,
    options: {},
    onFocus: () => setIsLabelObscured(true),
    onBlur: () => setIsLabelObscured(false),
    success: submitted,
    disabled: isReturningUser
  };

  return (
    <MobileContainer
      id="mobile-container"
      data-e2e="email-container"
      className={className}
    >
      <EmailInputWrapper id="input">
        <label
          htmlFor={id}
          css={css`
            ${hidden}
          `}
        >
          {isLabelObscured ? '' : labelText}
        </label>
        {input({ ...inputProps })}
      </EmailInputWrapper>
      {!submitted && !isReturningUser && button({ onClick: submit })}
      {!submitted &&
        isReturningUser &&
        returningUserButton({ onClick: () => VL.openModal() })}
    </MobileContainer>
  );
};

export function ViralLoopsWidget({
  id,
  buttonColor = 'ginger',
  buttonHoverColor = 'green',
  buttonText = 'Join Waitlist',
  returningUserButtonText = 'See Waitlist',
  setErrorMessage = () => {
    return false;
  }
}: WaitlistEmailCaptureProps) {
  return (
    <SubmitEmailWidget
      id={id}
      errorPosition={'bottom'}
      labelText={DEFAULT_EMAIL_LABEL}
      placeholderText={DEFAULT_EMAIL_LABEL}
      setErrorMessage={setErrorMessage}
      input={(props) => (
        <EmailWidgetInput
          data-e2e="waitlist-email-capture-input"
          data-testid="waitlist-email-capture-input"
          {...props}
        />
      )}
      button={(props) => (
        <MobileEmailButton
          cfButtonColor={buttonColor as CFColorTokenProp}
          cfButtonHoverColor={buttonHoverColor as CFColorTokenProp}
          {...props}
          data-e2e="waitlist-email-capture-button"
          data-testid="waitlist-email-capture-button"
        >
          {buttonText}
        </MobileEmailButton>
      )}
      returningUserButton={(props) => (
        <MobileEmailButton
          cfButtonColor={buttonColor as CFColorTokenProp}
          cfButtonHoverColor={buttonHoverColor as CFColorTokenProp}
          {...props}
          data-e2e="waitlist-email-capture-button"
          data-testid="waitlist-email-capture-button"
        >
          {returningUserButtonText}
        </MobileEmailButton>
      )}
    />
  );
}
